import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { useLanguage } from "../context/LanguageContext";

const KH = "'Khmer OS Siemreap','Noto Sans Khmer',sans-serif";
const MO = "'Moul','Noto Sans Khmer',serif";
const GOLD = "#E8B84A";
const CARD_W = 340;
const CARD_H = 200;
const GAP = 14;
const PAD = 6;

const findTarget = (target) => {
  const list = Array.isArray(target) ? target : [target];
  for (const sel of list) {
    const el = typeof sel === "function" ? sel() : (sel && document.querySelector(sel));
    if (el) return el;
  }
  return null;
};

// Step-by-step spotlight tour.
// steps: [{ target: cssSelector | [selectors], title, text, form? }]
// Texts are Khmer source strings, translated through t().
// onStep(index, step) fires whenever a step becomes active (lets the page open/close a modal).
export default function GuideTour({ steps, open, onClose, onStep }) {
  const { t } = useLanguage();
  const [index, setIndex] = useState(0);
  const [rect,  setRect]  = useState(null);
  const onStepRef = useRef(onStep);
  onStepRef.current = onStep;

  const step = steps[index];

  useEffect(() => { if (open) setIndex(0); }, [open]);

  useEffect(() => {
    if (open && step) onStepRef.current?.(index, step);
  }, [open, index]); // eslint-disable-line react-hooks/exhaustive-deps

  const measure = (scroll) => {
    const el = step && findTarget(step.target);
    if (!el) { setRect(null); return; }
    if (scroll) el.scrollIntoView({ block:"center", inline:"nearest" });
    const r = el.getBoundingClientRect();
    setRect({ top:r.top, left:r.left, width:r.width, height:r.height });
  };

  // modal content mounts a tick after onStep runs, so measure a few times
  useLayoutEffect(() => {
    if (!open) return;
    setRect(null);
    measure(true);
    const ids = [80, 250, 500].map(ms => setTimeout(() => measure(true), ms));
    return () => ids.forEach(clearTimeout);
  }, [open, index]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!open) return;
    const again = () => measure(false);
    window.addEventListener("resize", again);
    window.addEventListener("scroll", again, true);
    return () => {
      window.removeEventListener("resize", again);
      window.removeEventListener("scroll", again, true);
    };
  }, [open, index]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      else if (e.key === "ArrowRight") setIndex(i => Math.min(i + 1, steps.length - 1));
      else if (e.key === "ArrowLeft")  setIndex(i => Math.max(i - 1, 0));
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, steps.length, onClose]);

  if (!open || !step) return null;

  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const cardW = Math.min(CARD_W, vw - 32);
  const last  = index === steps.length - 1;

  let cardPos;
  if (rect) {
    const below = rect.top + rect.height + PAD + GAP;
    const above = rect.top - PAD - GAP - CARD_H;
    const left  = Math.min(Math.max(rect.left, 16), vw - cardW - 16);
    const sideTop   = Math.min(Math.max(rect.top, 16), vh - CARD_H - 16);
    const rightRoom = vw - (rect.left + rect.width + PAD) - GAP - 16;
    const leftRoom  = rect.left - PAD - GAP - 16;
    if (below + CARD_H < vh)       cardPos = { top: below, left };
    else if (above > 8)            cardPos = { top: above, left };
    else if (rightRoom >= cardW)   cardPos = { top: sideTop, left: rect.left + rect.width + PAD + GAP };
    else if (leftRoom >= cardW)    cardPos = { top: sideTop, left: rect.left - PAD - GAP - cardW };
    else                           cardPos = { bottom: 24, left: Math.max((vw - cardW) / 2, 16) };
  } else {
    cardPos = { top: Math.max((vh - CARD_H) / 2, 16), left: Math.max((vw - cardW) / 2, 16) };
  }

  const linkBtn = {
    background:"none", border:"none", cursor:"pointer", fontFamily:KH,
    fontSize:13, padding:"8px 12px", color:"var(--muted)",
  };

  return (
    <div style={{ position:"fixed", inset:0, zIndex:3000 }}>
      {rect ? (
        <div style={{
          position:"fixed",
          top:rect.top - PAD, left:rect.left - PAD,
          width:rect.width + PAD * 2, height:rect.height + PAD * 2,
          borderRadius:12, border:`2px solid ${GOLD}`,
          boxShadow:"0 0 0 100vmax rgba(0,0,0,0.72)",
          pointerEvents:"none", transition:"all .2s ease",
        }} />
      ) : (
        <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.72)" }} />
      )}

      <div role="dialog" aria-live="polite" style={{
        position:"fixed", ...cardPos, width:cardW, boxSizing:"border-box",
        background:"var(--surface)", color:"var(--text)", borderRadius:16,
        padding:"18px 20px 14px", boxShadow:"0 24px 60px rgba(0,0,0,0.35)",
      }}>
        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"baseline", gap:12 }}>
          <h3 style={{ fontSize:15, fontWeight:700, margin:0, fontFamily:MO, color:"var(--brown2)" }}>
            {t(step.title)}
          </h3>
          <span style={{ fontSize:12, color:"var(--muted)", whiteSpace:"nowrap" }}>
            {index + 1} / {steps.length}
          </span>
        </div>
        <p style={{ fontSize:13, lineHeight:1.7, margin:"8px 0 14px", fontFamily:KH, color:"var(--muted)" }}>
          {t(step.text)}
        </p>
        <div style={{ display:"flex", justifyContent:"flex-end", alignItems:"center", gap:2 }}>
          <button onClick={onClose} style={linkBtn}>{t("រំលង")}</button>
          {index > 0 && (
            <button onClick={() => setIndex(i => i - 1)} style={{ ...linkBtn, color:"var(--brown)" }}>
              {t("ថយក្រោយ")}
            </button>
          )}
          <button
            onClick={() => (last ? onClose() : setIndex(i => i + 1))}
            style={{ marginLeft:6, padding:"8px 18px", border:"none", borderRadius:10,
                     background:"var(--brown)", color:"#fff", cursor:"pointer",
                     fontSize:13, fontWeight:600, fontFamily:KH }}>
            {last ? t("បញ្ចប់") : t("បន្ទាប់")}
          </button>
        </div>
      </div>
    </div>
  );
}
